import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaSpotify, FaTiktok } from 'react-icons/fa';
import { SiLinktree } from 'react-icons/si';

const Sidebar = () => {
  const [isGetInvolvedOpen, setIsGetInvolvedOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const closeSidebar = () => {
    const sb = document.querySelector('.ul-sidebar');
    if (sb) sb.classList.remove('active');
  };

  // Auto-close sidebar when screen resizes past the desktop breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        closeSidebar();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleWhyWeNeedYou = (e) => {
    e.preventDefault();
    closeSidebar();
    if (location.pathname === '/') {
      setTimeout(() => {
        const element = document.getElementById('get-involved');
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 300);
    } else {
      navigate('/#get-involved');
    }
  };

  return (
    <div className="ul-sidebar">
      <div className="ul-sidebar-header" style={{paddingTop: '10px', paddingRight: '10px', paddingBottom: '10px', display: 'flex', justifyContent: 'flex-end'}}>
        <button
          className="ul-sidebar-closer"
          aria-label="Close menu"
          onClick={closeSidebar}
        >
          <i className="flaticon-close"></i>
        </button>
      </div>

      <div className="ul-sidebar-header-nav-wrapper d-block d-lg-none" style={{paddingTop: '10px'}}>
        <nav className="ul-sidebar-nav">
          <ul>
            <li><Link to="/" className="sidebar-nav-link" onClick={closeSidebar}>Home</Link></li>
            <li><Link to="/about" className="sidebar-nav-link" onClick={closeSidebar}>About</Link></li>
            <li><Link to="/team" className="sidebar-nav-link" onClick={closeSidebar}>Team</Link></li>
            <li><Link to="/blog" className="sidebar-nav-link" onClick={closeSidebar}>Pathway Guides</Link></li>
            <li><Link to="/pathio-buddies" className="sidebar-nav-link" onClick={closeSidebar}>Pathio Buddies</Link></li>
            <li className="sidebar-menu-item">
              <button 
                className="sidebar-dropdown-toggle"
                onClick={() => setIsGetInvolvedOpen(!isGetInvolvedOpen)}
                aria-expanded={isGetInvolvedOpen}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: 'var(--ul-black)'
                }}
              >
                <span>Get Involved</span>
                <i 
                  className={`flaticon-down-arrow ${isGetInvolvedOpen ? 'active' : ''}`}
                  style={{
                    transform: isGetInvolvedOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    fontSize: 'clamp(10px, 0.84vw, 12px)',
                    marginLeft: '8px',
                    opacity: 0.6
                  }}
                ></i>
              </button>
              {isGetInvolvedOpen && (
                <ul 
                  className="sidebar-submenu"
                  style={{
                    listStyle: 'none',
                    padding: 'clamp(8px, 0.84vw, 12px) 0 clamp(8px, 0.84vw, 12px) clamp(20px, 2.1vw, 28px)',
                    margin: 'clamp(8px, 0.84vw, 12px) 0 0 0',
                    background: 'rgba(20, 180, 180, 0.05)',
                    borderRadius: 'clamp(6px, 0.63vw, 8px)',
                    borderLeft: '3px solid var(--ul-primary)'
                  }}
                >
                  <li style={{marginBottom: 'clamp(6px, 0.63vw, 8px)'}}>
                    <a 
                      href="#get-involved" 
                      className="sidebar-nav-link" 
                      onClick={handleWhyWeNeedYou}
                      style={{
                        display: 'block',
                        padding: 'clamp(10px, 1.05vw, 14px) clamp(12px, 1.26vw, 16px)',
                        borderRadius: 'clamp(4px, 0.42vw, 6px)',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(20, 180, 180, 0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      Why We Need You
                    </a>
                  </li>
                  <li>
                    <Link 
                      to="/contact" 
                      className="sidebar-nav-link" 
                      onClick={closeSidebar}
                      style={{
                        display: 'block',
                        padding: 'clamp(10px, 1.05vw, 14px) clamp(12px, 1.26vw, 16px)',
                        borderRadius: 'clamp(4px, 0.42vw, 6px)',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(20, 180, 180, 0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      Contact
                    </Link>
                  </li>
                </ul>
              )}
            </li>
            <li><Link to="/donate" className="sidebar-nav-link" onClick={closeSidebar}>Donate</Link></li>
          </ul>
        </nav>
      </div>

      <div className="ul-sidebar-footer">
        <span className="ul-sidebar-footer-title">Follow us</span>

        <div className="ul-sidebar-footer-social">
          <a href="https://www.instagram.com/pathio_org" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="flaticon-instagram"></i></a>
          <a href="https://www.tiktok.com/@pathio_org" target="_blank" rel="noopener noreferrer" aria-label="TikTok"><FaTiktok /></a>
          <a href="https://open.spotify.com/user/31wi6czixyazxhzyardx4bcp3cui" target="_blank" rel="noopener noreferrer" aria-label="Spotify"><FaSpotify /></a>
          <a href="https://youtube.com/@pathio_org" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><i className="flaticon-youtube"></i></a>
          <a href="https://linkedin.com/company/pathio-org/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="flaticon-linkedin-big-logo"></i></a>
          <a href="https://linktr.ee/pathio.org" target="_blank" rel="noopener noreferrer" aria-label="Linktree"><SiLinktree /></a>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
